import { useEffect, useRef, useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext.tsx";
import { brandLogo, brandMark } from "@/components/atomic-crm/root/logos/brand";
import { SSOAuthButton } from "./SSOAuthButton";
import {
  disableEmailPasswordAuthentication,
  googleWorkplaceDomain,
} from "./authConfig";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const { title } = useConfigurationContext();
  const logo = brandLogo;
  const mark = brandMark;
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";

    if (!shouldNotify || hasDisplayedRecoveryNotification.current) {
      return;
    }

    hasDisplayedRecoveryNotification.current = true;
    notify("crm.auth.recovery_email_sent", {
      type: "success",
      messageArgs: {
        _: "If you're a registered user, you should receive a password recovery email shortly.",
      },
    });

    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <div className="relative grid w-full lg:grid-cols-2">
        <div className="relative hidden min-h-screen flex-col p-10 text-white lg:flex">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 80% 60% at 20% 10%, rgba(253,118,26,0.35), transparent 55%), radial-gradient(ellipse 50% 40% at 90% 90%, rgba(253,118,26,0.12), transparent 45%), #0f1626",
            }}
          />
          <div className="relative z-20 flex items-center gap-3 text-lg font-medium">
            <img className="h-10 w-auto" src={mark} alt={title} />
            <span className="text-white font-semibold">
              Georgia Web Co <span className="text-[#fd761a]">CRM</span>
            </span>
          </div>
          <div className="relative z-20 mt-auto max-w-md">
            <p className="text-3xl font-semibold leading-tight tracking-tight text-white">
              Organize customers, jobs, and follow-ups
            </p>
            <p className="mt-4 text-base text-white/80 leading-relaxed">
              Simple CRM for local service businesses — powered by Georgia Web
              Co.
            </p>
          </div>
        </div>
        <div className="flex flex-col justify-center w-full px-6 py-10 sm:px-10 lg:p-12 bg-[#ffffff] text-[#0f1626]">
          <div className="w-full space-y-6 max-w-[420px] mx-auto">
            <div className="flex items-center gap-3 lg:hidden mb-2">
              <img
                className="h-10 w-auto max-w-[220px] object-contain"
                src={logo}
                alt={title}
              />
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight text-[#0f1626]">
                {translate("ra.auth.sign_in")}
              </h1>
              <p className="text-base text-[#3d4654]">
                Sign in to Georgia Web Co CRM
              </p>
            </div>
            <div className="rounded-xl border-2 border-[#c5ceda] bg-white p-6 shadow-[0_12px_32px_rgba(15,22,38,0.1)] space-y-6">
            {disableEmailPasswordAuthentication ? null : (
              <Form className="space-y-8" onSubmit={handleSubmit}>
                <TextInput
                  label="ra.auth.email"
                  source="email"
                  type="email"
                  validate={required()}
                />
                <TextInput
                  label="ra.auth.password"
                  source="password"
                  type="password"
                  validate={required()}
                />
                <div className="flex flex-col gap-4">
                  <Button
                    type="submit"
                    className="cursor-pointer w-full h-11 bg-[#fd761a] hover:bg-[#b84a00] text-white"
                    disabled={loading}
                  >
                    {translate("ra.auth.sign_in")}
                  </Button>
                </div>
              </Form>
            )}
            {googleWorkplaceDomain ? (
              <SSOAuthButton className="w-full" domain={googleWorkplaceDomain}>
                {translate("crm.auth.sign_in_google_workspace", {
                  _: "Sign in with Google Workplace",
                })}
              </SSOAuthButton>
            ) : null}
            {disableEmailPasswordAuthentication ? null : (
              <Link
                to={"/forgot-password"}
                className="block text-sm text-center hover:underline text-muted-foreground"
              >
                {translate("ra-supabase.auth.forgot_password", {
                  _: "Forgot password?",
                })}
              </Link>
            )}
            </div>
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};
